import type { IStudyRepository } from "../repositories/StudyRepository";
import type {
  Study,
  CreateStudyRequest,
  UpdateStudyRequest,
  StudyFilters,
  StudyListResponse,
  StudyStatistics,
} from "../pages/StudyManagement/types/study.type";
import {
  STUDY_PHASE_VALUES,
  API_TO_DISPLAY_PHASE_MAPPING,
} from "../pages/StudyManagement/constants";
import { getErrorMessage, isApiError } from "../utils/errorMessage";
import { STUDY_STATUSES, isStudyStatus } from "@/types/studyStatus.type";
export interface IStudyUseCase {
  createStudy(study: CreateStudyRequest): Promise<Study>;
  getStudies(
    filters?: StudyFilters,
    page?: number,
    limit?: number,
  ): Promise<StudyListResponse>;
  getAllStudies(filters?: StudyFilters): Promise<Study[]>;
  getStudyById(id: string): Promise<Study>;
  updateStudy(id: string, updates: UpdateStudyRequest): Promise<Study>;
  deleteStudy(id: string): Promise<void>;
  activateStudy(id: string): Promise<Study>;
  archiveStudy(id: string): Promise<Study>;
  getStudyStatistics(): Promise<StudyStatistics>;
}
export class StudyUseCase implements IStudyUseCase {
  constructor(private studyRepository: IStudyRepository) {}
  async createStudy(study: CreateStudyRequest): Promise<Study> {
    // Validate required fields
    if (!study.title?.trim()) {
      throw new Error("Study title is required");
    }
    if (!study.protocol_number?.trim()) {
      throw new Error("Protocol number is required");
    }
    if (!study.sponsor_name?.trim()) {
      throw new Error("Sponsor name is required");
    }
    if (!study.start_date) {
      throw new Error("Start date is required");
    }
    if (!study.end_date) {
      throw new Error("End date is required");
    }
    if (!study.tenant_id?.trim()) {
      throw new Error("Tenant ID is required");
    }
    // Validate date logic
    const startDate = new Date(study.start_date);
    const endDate = new Date(study.end_date);
    if (startDate >= endDate) {
      throw new Error("End date must be after start date");
    }
    // Validate study phase
    const validApiPhases = Object.keys(API_TO_DISPLAY_PHASE_MAPPING);
    const validDisplayPhases = STUDY_PHASE_VALUES;
    // Check if phase is in API format or display format
    const phase = String(study.phase);
    if (
      !validApiPhases.includes(phase) &&
      !(validDisplayPhases as readonly string[]).includes(phase)
    ) {
      throw new Error("Invalid study phase");
    }
    try {
      return await this.studyRepository.createStudy(study);
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        const msg = getErrorMessage(error, "Authentication failed");
        throw new Error(
          msg.includes("Kong")
            ? "Authentication failed: Missing API gateway headers"
            : "Authentication failed: Please login again",
        );
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to create study",
        );
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(getErrorMessage(error, "Invalid study data provided"));
      }
      throw new Error(getErrorMessage(error, "Failed to create study"));
    }
  }
  async getStudies(
    filters?: StudyFilters,
    page = 1,
    limit = 10,
  ): Promise<StudyListResponse> {
    // Validate pagination parameters
    if (page < 1) {
      throw new Error("Page number must be greater than 0");
    }
    if (limit < 1 || limit > 100) {
      throw new Error("Limit must be between 1 and 100");
    }
    return await this.studyRepository.getStudies(filters, page, limit);
  }
  async getAllStudies(filters?: StudyFilters): Promise<Study[]> {
    return await this.studyRepository.getAllStudies(filters);
  }
  async getStudyById(id: string): Promise<Study> {
    if (!id?.trim()) {
      throw new Error("Study ID is required");
    }
    return await this.studyRepository.getStudyById(id);
  }
  async updateStudy(id: string, updates: UpdateStudyRequest): Promise<Study> {
    if (!id?.trim()) {
      throw new Error("Study ID is required");
    }
    // Validate date logic if both dates are provided
    if (updates.start_date && updates.end_date) {
      const startDate = new Date(updates.start_date);
      const endDate = new Date(updates.end_date);
      if (startDate >= endDate) {
        throw new Error("End date must be after start date");
      }
    }
    // Validate study phase if provided
    if (updates.phase) {
      const validApiPhases = Object.keys(API_TO_DISPLAY_PHASE_MAPPING);
      const validDisplayPhases = STUDY_PHASE_VALUES;
      // Check if phase is in API format or display format
      if (
        !validApiPhases.includes(updates.phase) &&
        !validDisplayPhases.includes(updates.phase)
      ) {
        throw new Error("Invalid study phase");
      }
    }
    return await this.studyRepository.updateStudy(id, updates);
  }
  async deleteStudy(id: string): Promise<void> {
    if (!id?.trim()) {
      throw new Error("Study ID is required");
    }
    const study = await this.studyRepository.getStudyById(id);
    // Only allow deletion of Draft studies
    if (!isStudyStatus(study.status, STUDY_STATUSES.DRAFT)) {
      throw new Error("Only studies in Draft status can be deleted");
    }
    await this.studyRepository.deleteStudy(id);
  }
  async activateStudy(id: string): Promise<Study> {
    if (!id?.trim()) {
      throw new Error("Study ID is required");
    }
    const study = await this.studyRepository.getStudyById(id);
    // Only allow activation of Draft studies
    if (!isStudyStatus(study.status, STUDY_STATUSES.DRAFT)) {
      throw new Error("Only studies in Draft status can be activated");
    }
    return await this.studyRepository.activateStudy(id);
  }
  async archiveStudy(id: string): Promise<Study> {
    if (!id?.trim()) {
      throw new Error("Study ID is required");
    }
    const study = await this.studyRepository.getStudyById(id);
    // Only allow archiving of Active or Draft studies (not already archived)
    if (isStudyStatus(study.status, STUDY_STATUSES.ARCHIVED)) {
      throw new Error("Study is already archived");
    }
    return await this.studyRepository.archiveStudy(id);
  }
  async getStudyStatistics(): Promise<StudyStatistics> {
    return await this.studyRepository.getStudyStatistics();
  }
}
